import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { toast } from '../lib/safeToast';
import {
  Users,
  Plus,
  Minus,
  Trash2,
  Edit2,
  Shield,
  UserCheck,
  UserX,
  Loader2,
  Mail,
  Copy,
  Store,
  ChevronDown,
  ChevronRight,
  List,
  FolderTree,
  Pencil,
  Ban,
  FileCheck,
  DollarSign,
  Settings,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { getApiErrorMessage } from '../lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';

const ADDITIONAL_PERMISSIONS = [
  { code: 'compliance_officer', label: 'Compliance Officer', desc: 'KYC/KYB review & approval' },
  { code: 'accounts', label: 'Accounts', desc: 'Financial data & reconciliation' },
  { code: 'operations', label: 'Operations', desc: 'Merchant & user management' },
];

const PermissionsSection = ({ user, onUpdate, api }) => {
  const [saving, setSaving] = useState(false);
  const current = user?.permissions || [];

  const togglePerm = async (code) => {
    const updated = current.includes(code) ? current.filter(p => p !== code) : [...current, code];
    setSaving(true);
    try {
      await api.put(`/users/${user.id}/permissions`, { permissions: updated });
      toast.success('Permissions updated');
      onUpdate();
    } catch (err) {
      toast.error('Failed to update permissions');
    } finally {
      setSaving(false);
    }
  };

  if (user?.role === 'admin') return null;

  return (
    <div>
      <p className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-2">Additional Access</p>
      <div className="space-y-1.5">
        {ADDITIONAL_PERMISSIONS.filter(p => p.code !== user?.role).map(perm => (
          <label key={perm.code} className="flex items-start gap-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={current.includes(perm.code)}
              onChange={() => togglePerm(perm.code)}
              disabled={saving}
              className="rounded border-slate-300 text-[#0B6445] focus:ring-[#0B6445] w-4 h-4 mt-0.5"
            />
            <div className="flex-1 min-w-0">
              <span className="text-sm font-medium text-[#0F172A] group-hover:text-[#0B6445]">{perm.label}</span>
              <div className="text-xs text-slate-500 leading-tight">{perm.desc}</div>
            </div>
          </label>
        ))}
      </div>
      {current.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {current.map(p => (
            <span key={p} className="text-[10px] bg-[#E6F7F3] text-[#073B2E] px-1.5 py-0.5 rounded font-medium">
              {ADDITIONAL_PERMISSIONS.find(x => x.code === p)?.label || p}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const UserManagement = ({ LayoutComponent = Layout }) => {
  const { api, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createLoading, setCreateLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newUserResult, setNewUserResult] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'viewer',
    merchant_id: '',
  });
  const [merchants, setMerchants] = useState([]);
  const [detailUser, setDetailUser] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'byCompany'
  const [expandedCompanies, setExpandedCompanies] = useState(() => new Set()); // set of merchant_id for expanded rows
  const [merchantPopoverOpen, setMerchantPopoverOpen] = useState(false);
  const [expandedMerchantIds, setExpandedMerchantIds] = useState(() => new Set()); // for tree dropdown
  const [detailOrganisationId, setDetailOrganisationId] = useState(''); // for SU assign organisation in user detail
  const [organisationSaving, setOrganisationSaving] = useState(false);
  const [detailModifyOpen, setDetailModifyOpen] = useState(false);
  const [detailEditName, setDetailEditName] = useState('');
  const [detailEditRole, setDetailEditRole] = useState('');
  const [detailActionLoading, setDetailActionLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const canAccessBackOfficeUsers = currentUser?.role === 'admin' || currentUser?.role === 'operations' || (currentUser?.permissions || []).includes('operations');
  const isSU = currentUser?.role === 'admin';
  const isOperations = currentUser?.role === 'operations' || (currentUser?.permissions || []).includes('operations');
  const isMerchantUser = (u) => ['merchant_admin', 'manager', 'viewer'].includes(u?.role || '');
  const canChangeRole = (targetUser) => {
    if (targetUser?.id === currentUser?.id) return false;
    if (isSU) return true;
    if (isOperations) return isMerchantUser(targetUser);
    if (['merchant_admin', 'manager'].includes(currentUser?.role) && targetUser?.merchant_id === currentUser?.merchant_id) return true;
    return false;
  };
  const getRoleOptionsForUser = (targetUser) => {
    if (isSU) return ['viewer', 'manager', 'merchant_admin', 'operations', 'accounts', 'compliance_officer', 'admin'];
    return ['viewer', 'manager', 'merchant_admin'];
  };
  useEffect(() => {
    if ((showCreate || detailUser) && canAccessBackOfficeUsers) {
      api.get('/backoffice/merchants-list').then((r) => setMerchants(r.data?.data || [])).catch(() => setMerchants([]));
    }
  }, [showCreate, detailUser, canAccessBackOfficeUsers, api]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/users');
      setUsers(response.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    const needsMerchant = canAccessBackOfficeUsers && (
      isOperations
        ? ['viewer', 'manager', 'merchant_admin'].includes(formData.role)
        : (formData.role === 'viewer' || formData.role === 'manager')
    );
    if (needsMerchant && !formData.merchant_id) {
      toast.error('Please select an organisation.');
      return;
    }
    setCreateLoading(true);
    try {
      const payload = { email: formData.email, name: formData.name, role: formData.role };
      if (needsMerchant && formData.merchant_id) {
        payload.merchant_id = formData.merchant_id;
      }
      const response = await api.post('/users', payload);
      setNewUserResult(response.data.data);
      toast.success('User created successfully');
      setFormData({ email: '', name: '', role: 'viewer', merchant_id: '' });
      fetchUsers();
    } catch (error) {
      toast.error(getApiErrorMessage(error.response?.data?.detail, 'Failed to create user'));
    } finally {
      setCreateLoading(false);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await api.put(`/users/${userId}/role`, { role: newRole });
      toast.success('Role updated successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const handleAdminDisable2FA = async (userId) => {
    try {
      await api.put(`/users/${userId}/2fa`, { enabled: false });
      toast.success('2FA disabled. User can log in and set it up again in Settings.');
      fetchUsers();
      if (detailUser?.id === userId) {
        const res = await api.get(`/users/${userId}`);
        setDetailUser(res.data?.data || null);
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error.response?.data?.detail, 'Failed to update 2FA'));
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      toast.success('User deleted');
      setDetailUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(getApiErrorMessage(error.response?.data?.detail, 'Failed to delete user'));
    }
  };

  const openUserDetail = async (userId) => {
    setDetailUser(null);
    setDetailOrganisationId('');
    setDetailModifyOpen(false);
    setDetailLoading(true);
    try {
      const res = await api.get(`/users/${userId}`);
      const data = res.data?.data || null;
      setDetailUser(data);
      if (data?.merchant_id) setDetailOrganisationId(data.merchant_id);
      setDetailEditName(data?.name ?? '');
      setDetailEditRole(data?.role ?? '');
    } catch {
      toast.error('Failed to load user');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDetailModify = async (e) => {
    e.preventDefault();
    if (!detailUser?.id) return;
    setDetailActionLoading(true);
    try {
      if (detailEditName !== (detailUser.name || '')) {
        await api.patch(`/users/${detailUser.id}`, { name: detailEditName.trim() });
        toast.success('Name updated');
      }
      if (detailEditRole && detailEditRole !== detailUser.role) {
        await api.put(`/users/${detailUser.id}/role`, { role: detailEditRole });
        toast.success('Role updated');
      }
      setDetailModifyOpen(false);
      openUserDetail(detailUser.id);
      fetchUsers();
    } catch (err) {
      toast.error(getApiErrorMessage(err.response?.data?.detail, 'Update failed'));
    } finally {
      setDetailActionLoading(false);
    }
  };

  const handleDetailSuspend = async () => {
    if (!detailUser?.id) return;
    const next = detailUser.account_status === 'suspended' ? 'active' : 'suspended';
    setDetailActionLoading(true);
    try {
      await api.patch(`/users/${detailUser.id}`, { account_status: next });
      toast.success(next === 'suspended' ? 'User suspended' : 'User activated');
      openUserDetail(detailUser.id);
      fetchUsers();
    } catch (err) {
      toast.error(getApiErrorMessage(err.response?.data?.detail, 'Action failed'));
    } finally {
      setDetailActionLoading(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (!detailUser?.id || newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setPasswordSaving(true);
    try {
      await api.put(`/users/${detailUser.id}/password`, { new_password: newPassword });
      toast.success('Password updated');
      setNewPassword('');
      setConfirmPassword('');
      setChangePasswordOpen(false);
      openUserDetail(detailUser.id);
      fetchUsers();
    } catch (err) {
      toast.error(getApiErrorMessage(err.response?.data?.detail, 'Failed to update password'));
    } finally {
      setPasswordSaving(false);
    }
  };

  const copyToClipboard = async (text, label = 'Value') => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error('Copy failed');
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: 'Super Admin', compliance_officer: 'Compliance Officer',
      accounts: 'Accounts', operations: 'Operations',
      merchant_admin: 'Merchant Admin', manager: 'Manager', viewer: 'Viewer',
    };
    return labels[role] || (role || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const groupUsersByCompany = () => {
    const map = {};
    users.forEach((u) => {
      const key = u.merchant_id || '—';
      if (!map[key]) map[key] = { company_name: u.company_name || '—', merchant_id: key, users: [] };
      map[key].users.push(u);
    });
    return Object.values(map).sort((a, b) => (a.company_name || '').localeCompare(b.company_name || ''));
  };

  const toggleCompanyExpanded = (key) => {
    setExpandedCompanies((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const MASTER_ORGANISATION_NAME = 'Master organisation';
  const merchantsSorted = React.useMemo(() => {
    const list = [...(merchants || [])];
    list.sort((a, b) => {
      const an = (a.name || '').toLowerCase();
      const bn = (b.name || '').toLowerCase();
      if (an === MASTER_ORGANISATION_NAME.toLowerCase()) return -1;
      if (bn === MASTER_ORGANISATION_NAME.toLowerCase()) return 1;
      return an.localeCompare(bn);
    });
    return list;
  }, [merchants]);
  const merchantsForOpsCreate = React.useMemo(() => {
    if (!isOperations || isSU) return merchantsSorted;
    return merchantsSorted.filter((m) => (m.name || '').toLowerCase() !== MASTER_ORGANISATION_NAME.toLowerCase());
  }, [merchantsSorted, isOperations, isSU]);

  const merchantsWithUsers = React.useMemo(() => {
    return merchantsSorted.map((m) => ({
      ...m,
      users: (users || []).filter((u) => u.merchant_id === m.id),
    }));
  }, [merchantsSorted, users]);

  const toggleMerchantExpand = (mid) => {
    setExpandedMerchantIds((prev) => {
      const next = new Set(prev);
      if (next.has(mid)) next.delete(mid);
      else next.add(mid);
      return next;
    });
  };

  const handleUpdateOrganisation = async () => {
    if (!detailUser?.id || !detailOrganisationId || detailOrganisationId === detailUser.merchant_id) return;
    setOrganisationSaving(true);
    try {
      await api.patch(`/users/${detailUser.id}`, { merchant_id: detailOrganisationId });
      toast.success('Organisation updated');
      setDetailOrganisationId('');
      openUserDetail(detailUser.id);
      fetchUsers();
    } catch (err) {
      toast.error(getApiErrorMessage(err.response?.data?.detail, 'Failed to update organisation'));
    } finally {
      setOrganisationSaving(false);
    }
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-[#1C472B] text-white hover:bg-[#1C472B]';
      case 'compliance_officer':
        return 'bg-indigo-600 text-white hover:bg-indigo-600';
      case 'accounts':
        return 'bg-blue-600 text-white hover:bg-blue-600';
      case 'operations':
        return 'bg-amber-600 text-white hover:bg-amber-600';
      case 'merchant_admin':
        return 'bg-[#0B6445] text-white hover:bg-[#0B6445]';
      case 'manager':
        return 'bg-[#A5E0D2] text-[#0F2818] hover:bg-[#A5E0D2]';
      default:
        return 'bg-slate-100 text-slate-700 hover:bg-slate-100';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <LayoutComponent>
      <div data-testid="user-management-page" className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-extrabold text-[#0F172A] ">
              User Management
            </h1>
            <p className="text-slate-500 mt-1">Manage team members and their roles</p>
          </div>
          <Dialog open={showCreate} onOpenChange={(open) => {
            setShowCreate(open);
            if (!open) setNewUserResult(null);
          }}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="add-user-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold ">
                  {newUserResult ? 'User Created' : 'Add New User'}
                </DialogTitle>
                <DialogDescription>
                  {newUserResult
                    ? 'Share the temporary password with the user'
                    : 'Create a new team member account'}
                </DialogDescription>
              </DialogHeader>

              {newUserResult ? (
                <div className="space-y-4 mt-4">
                  <p className="text-sm text-slate-600">User created. Share these credentials with the user.</p>
                  <div className="rounded-xl border border-[#DCE4E0] bg-[#F8FAFC] overflow-hidden">
                    <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#DCE4E0]">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Email</p>
                        <p className="font-mono text-sm text-[#0F172A] truncate" title={newUserResult.email}>{newUserResult.email}</p>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="shrink-0 border-[#0B6445] text-[#0B6445] hover:bg-[#E6F7F3] hover:text-[#073B2E]"
                        onClick={() => copyToClipboard(newUserResult.email, 'Email')}
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        Copy
                      </Button>
                    </div>
                    <div className="flex items-center justify-between gap-3 px-4 py-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Temporary password</p>
                        <p className="font-mono text-sm text-[#0F172A] break-all" title={newUserResult.temp_password}>{newUserResult.temp_password}</p>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="shrink-0 border-[#0B6445] text-[#0B6445] hover:bg-[#E6F7F3] hover:text-[#073B2E]"
                        onClick={() => copyToClipboard(newUserResult.temp_password, 'Password')}
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        Copy
                      </Button>
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="w-full border-[#0B6445] text-[#0B6445]"
                    onClick={() => copyToClipboard(`Email: ${newUserResult.email}\nTemporary password: ${newUserResult.temp_password}`, 'All details')}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy all details
                  </Button>
                  <Button
                    onClick={() => {
                      setShowCreate(false);
                      setNewUserResult(null);
                    }}
                    className="btn-primary w-full"
                  >
                    Done
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
                  <div>
                    <Label className="input-label">Full Name</Label>
                    <Input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="John Doe"
                      className="input-field"
                      required
                      data-testid="new-user-name"
                    />
                  </div>

                  <div>
                    <Label className="input-label">Email Address</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@company.com"
                      className="input-field"
                      required
                      data-testid="new-user-email"
                    />
                  </div>

                  <div>
                    <Label className="input-label">Role</Label>
                    <Select
                      value={formData.role}
                      onValueChange={(value) => setFormData({ ...formData, role: value })}
                    >
                      <SelectTrigger className="h-12" data-testid="new-user-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="viewer">
                          <div className="flex items-center gap-2">
                            <UserCheck className="w-4 h-4" />
                            Viewer - Read-only access
                          </div>
                        </SelectItem>
                        <SelectItem value="manager">
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            Manager - Create payments
                          </div>
                        </SelectItem>
                        <SelectItem value="merchant_admin">
                          <div className="flex items-center gap-2">
                            <Store className="w-4 h-4" />
                            Merchant Admin - Full app, no Back Office
                          </div>
                        </SelectItem>
                        {currentUser?.role === 'admin' && (
                          <SelectItem value="admin">
                            <div className="flex items-center gap-2">
                              <Shield className="w-4 h-4" />
                              Super Admin - Back Office access
                            </div>
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  {canAccessBackOfficeUsers && (
                    isOperations
                      ? ['viewer', 'manager', 'merchant_admin'].includes(formData.role)
                      : (formData.role === 'viewer' || formData.role === 'manager')
                  ) && (
                    <div>
                      <Label className="input-label">Organisation {isOperations ? '(required)' : '(required)'}</Label>
                      <Popover open={merchantPopoverOpen} onOpenChange={setMerchantPopoverOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            className="h-12 w-full justify-between font-normal"
                            data-testid="new-user-merchant"
                          >
                            {formData.merchant_id
                              ? (merchants.find((m) => m.id === formData.merchant_id)?.name || formData.merchant_id)
                              : 'Select organisation...'}
                            <ChevronDown className="h-4 w-4 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[var(--radix-popover-trigger-width)] max-h-[320px] overflow-y-auto p-0" align="start">
                          <div className="py-1">
                            {(isOperations && !isSU ? merchantsForOpsCreate : merchantsWithUsers).map((m) => {
                              const isExpanded = expandedMerchantIds.has(m.id);
                              const isMasterOrg = (m.name || '').toLowerCase() === MASTER_ORGANISATION_NAME.toLowerCase();
                              return (
                                <div key={m.id} className="border-b border-[#DCE4E0]/60 last:border-b-0">
                                  <div className="flex items-center min-w-0">
                                    <button
                                      type="button"
                                      onClick={() => toggleMerchantExpand(m.id)}
                                      className="shrink-0 p-2 text-slate-500 hover:bg-slate-100 rounded"
                                      aria-label={isExpanded ? 'Collapse' : 'Expand'}
                                    >
                                      {isExpanded ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setFormData({ ...formData, merchant_id: m.id });
                                        setMerchantPopoverOpen(false);
                                      }}
                                      className={`flex-1 text-left px-2 py-2.5 rounded hover:bg-[#E6F7F3] text-sm font-medium truncate ${formData.merchant_id === m.id ? 'bg-[#E6F7F3] text-[#073B2E]' : 'text-[#0F172A]'}`}
                                    >
                                      {isMasterOrg && <Shield className="w-4 h-4 inline-block mr-1.5 text-[#0B6445] shrink-0 align-middle" />}
                                      {!isMasterOrg && <Store className="w-4 h-4 inline-block mr-1.5 text-slate-400 shrink-0 align-middle" />}
                                      {m.name || m.id}
                                    </button>
                                  </div>
                                  {isExpanded && m.users && m.users.length > 0 && (
                                    <div className="bg-slate-50/80 pl-8 pr-2 py-1.5 space-y-0.5">
                                      {m.users.map((u) => (
                                        <div key={u.id} className="text-xs text-slate-600 py-1 truncate">
                                          <span className="font-medium text-slate-800">{u.name || '—'}</span>
                                          <span className="text-slate-400 mx-1">·</span>
                                          <span className="text-slate-500">{u.email}</span>
                                          <span className="ml-1 text-slate-400">({getRoleLabel(u.role)})</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </PopoverContent>
                      </Popover>
                      <p className="text-xs text-slate-500 mt-1">User will be assigned to this organisation. Expand (+) to see existing users.</p>
                    </div>
                  )}

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowCreate(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="btn-primary flex-1"
                      disabled={createLoading}
                      data-testid="create-user-btn"
                    >
                      {createLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Create User'
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </DialogContent>
          </Dialog>

          {/* User detail: temp password or Change password */}
          <Dialog open={!!detailUser} onOpenChange={(open) => { if (!open) { setDetailUser(null); setChangePasswordOpen(false); setNewPassword(''); setConfirmPassword(''); setDetailOrganisationId(''); setDetailModifyOpen(false); } }}>
            <DialogContent className="sm:max-w-xl">
              <DialogHeader>
                <DialogTitle className="text-[#0F172A]">User details</DialogTitle>
                <DialogDescription className="text-slate-600">Credentials, permissions, and password options</DialogDescription>
              </DialogHeader>
              {detailLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-[#0B6445]" />
                </div>
              ) : detailUser ? (
                <div className="space-y-4 mt-2">
                  {/* Top section — 2-column grid */}
                  <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                    <div>
                      <p className="text-xs font-medium text-slate-600 uppercase tracking-wide">Name</p>
                      <p className="font-medium text-[#0F172A] mt-0.5">{detailUser.name || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-slate-600 uppercase tracking-wide">Email</p>
                      <p className="font-mono text-sm text-[#0F172A] mt-0.5 truncate">{detailUser.email}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-slate-600 uppercase tracking-wide">Role</p>
                      <div className="mt-0.5">
                        <Badge className={getRoleBadgeClass(detailUser.role)}>{getRoleLabel(detailUser.role)}</Badge>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-slate-600 uppercase tracking-wide">2FA</p>
                      <div className="mt-0.5 flex items-center gap-2">
                        {detailUser.is_2fa_enabled ? (
                          <>
                            <span className="text-[#1C472B] flex items-center gap-1"><Shield className="w-4 h-4" />Enabled</span>
                            {currentUser?.role === 'admin' && detailUser.id !== currentUser?.id && (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-900 h-7 text-xs"
                                onClick={() => handleAdminDisable2FA(detailUser.id)}
                              >
                                Disable
                              </Button>
                            )}
                          </>
                        ) : (
                          <span className="text-slate-400">Disabled</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Organisation + Additional Access — side by side */}
                  {canAccessBackOfficeUsers && (
                    <div className="grid grid-cols-2 gap-x-6 gap-y-3 pt-3 border-t border-[#DCE4E0]">
                      <div>
                        <p className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-1.5">Organisation</p>
                        {detailUser.merchant_id && detailUser.company_name ? (
                          <p className="text-sm text-[#0F172A]">{detailUser.company_name || '—'}</p>
                        ) : (
                          <>
                            <Select value={detailOrganisationId || ''} onValueChange={setDetailOrganisationId}>
                              <SelectTrigger className="h-9 bg-white text-sm">
                                <SelectValue placeholder="Assign organisation…" />
                              </SelectTrigger>
                              <SelectContent>
                                {(isOperations && !isSU ? merchantsForOpsCreate : merchantsSorted).map((m) => (
                                  <SelectItem key={m.id} value={m.id}>{m.name || m.id}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button
                              type="button"
                              size="sm"
                              className="mt-2 border-[#0B6445] text-[#0B6445] hover:bg-[#E6F7F3] hover:text-[#073B2E] h-8 text-xs"
                              onClick={handleUpdateOrganisation}
                              disabled={organisationSaving || !detailOrganisationId}
                            >
                              {organisationSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Assign'}
                            </Button>
                          </>
                        )}
                      </div>
                      <div>
                        <PermissionsSection user={detailUser} onUpdate={() => { openUserDetail(detailUser.id); fetchUsers(); }} api={api} />
                      </div>
                    </div>
                  )}

                  {/* Action buttons */}
                  {canAccessBackOfficeUsers && (
                    <div className="flex flex-wrap gap-2 pt-3 border-t border-[#DCE4E0]">
                      {canChangeRole(detailUser) && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900 shrink-0"
                        onClick={() => { setDetailModifyOpen(true); setDetailEditName(detailUser.name || ''); setDetailEditRole(detailUser.role || ''); }}
                        disabled={detailActionLoading}
                      >
                        <Pencil className="w-4 h-4 mr-1.5" />
                        Modify
                      </Button>
                      )}
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className={detailUser.account_status === 'suspended' ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-900 shrink-0' : 'border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-900 shrink-0'}
                        onClick={handleDetailSuspend}
                        disabled={detailActionLoading || detailUser.id === currentUser?.id}
                      >
                        <Ban className="w-4 h-4 mr-1.5" />
                        {detailUser.account_status === 'suspended' ? 'Activate' : 'Suspend'}
                      </Button>
                    </div>
                  )}
                  {detailModifyOpen && (
                    <form onSubmit={handleDetailModify} className="rounded-xl border border-[#DCE4E0] bg-[#F8FAFC] p-4 space-y-3">
                      <Label className="text-slate-700">Name</Label>
                      <Input value={detailEditName} onChange={(e) => setDetailEditName(e.target.value)} className="bg-white" />
                      <Label className="text-slate-700">Role</Label>
                      <Select value={detailEditRole} onValueChange={setDetailEditRole}>
                        <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {getRoleOptionsForUser(detailUser).map((r) => (
                            <SelectItem key={r} value={r}>{getRoleLabel(r)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="flex gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={() => setDetailModifyOpen(false)} className="border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900">Cancel</Button>
                        <Button type="submit" size="sm" disabled={detailActionLoading}>{detailActionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}</Button>
                      </div>
                    </form>
                  )}
                  {detailUser.temp_password ? (
                    <div className="mt-5 rounded-xl border border-[#DCE4E0] bg-[#F8FAFC] p-4">
                      <p className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-1">Temporary password</p>
                      <p className="text-sm text-slate-600 mb-3">User has not changed password yet. Share this with them.</p>
                      <div className="flex items-center justify-between gap-2">
                        <code className="font-mono text-sm break-all flex-1 text-[#0F172A]">{detailUser.temp_password}</code>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="shrink-0 border-[#0B6445] text-[#0B6445] hover:bg-[#E6F7F3] hover:text-[#073B2E]"
                          onClick={() => copyToClipboard(detailUser.temp_password, 'Password')}
                        >
                          <Copy className="w-4 h-4 mr-1" />
                          Copy
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-5">
                      {!changePasswordOpen ? (
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full border-[#0B6445] text-[#0B6445] hover:bg-[#E6F7F3] hover:text-[#073B2E] font-medium"
                          onClick={() => setChangePasswordOpen(true)}
                        >
                          Change password
                        </Button>
                      ) : (
                        <form onSubmit={handleSetPassword} className="space-y-3 rounded-xl border border-[#DCE4E0] p-4 bg-[#F8FAFC]">
                          <Label>New password</Label>
                          <Input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Min 8 characters"
                            minLength={8}
                            required
                          />
                          <Label>Confirm password</Label>
                          <Input
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Repeat new password"
                            minLength={8}
                            required
                          />
                          <div className="flex gap-2">
                            <Button type="button" variant="outline" onClick={() => { setChangePasswordOpen(false); setNewPassword(''); setConfirmPassword(''); }} className="hover:bg-slate-100 hover:text-slate-900 border-slate-300 text-slate-700">
                              Cancel
                            </Button>
                            <Button type="submit" className="btn-primary" disabled={passwordSaving}>
                              {passwordSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update password'}
                            </Button>
                          </div>
                        </form>
                      )}
                    </div>
                  )}
                </div>
              ) : null}
            </DialogContent>
          </Dialog>
        </div>

        {/* Role Legend */}
        <Card className="card">
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-2">
                <Badge className={getRoleBadgeClass('admin')}>Super Admin</Badge>
                <span className="text-sm text-slate-500">Full platform access, system config</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={getRoleBadgeClass('compliance_officer')}>Compliance Officer</Badge>
                <span className="text-sm text-slate-500">KYC/KYB review, read-only</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={getRoleBadgeClass('accounts')}>Accounts</Badge>
                <span className="text-sm text-slate-500">Financial data, reconciliation</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={getRoleBadgeClass('operations')}>Operations</Badge>
                <span className="text-sm text-slate-500">Merchant & user management</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={getRoleBadgeClass('merchant_admin')}>Merchant Admin</Badge>
                <span className="text-sm text-slate-500">Full merchant dashboard, own org</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={getRoleBadgeClass('manager')}>Manager</Badge>
                <span className="text-sm text-slate-500">Create payments & accounts</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={getRoleBadgeClass('viewer')}>Viewer</Badge>
                <span className="text-sm text-slate-500">Read-only access</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card className="card">
          <CardHeader className="flex flex-row items-center justify-between gap-4">
            <CardTitle className="text-lg font-semibold ">
              Team Members ({users.length})
            </CardTitle>
            {canAccessBackOfficeUsers && (
              <div className="flex rounded-lg border border-[#DCE4E0] p-0.5 bg-slate-50/50">
                <button
                  type="button"
                  onClick={() => setViewMode('table')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${viewMode === 'table' ? 'bg-white text-[#0B6445] shadow-sm' : 'text-slate-600 hover:text-[#073B2E]'}`}
                >
                  <List className="w-4 h-4" />
                  Table
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('byCompany')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${viewMode === 'byCompany' ? 'bg-white text-[#0B6445] shadow-sm' : 'text-slate-600 hover:text-[#073B2E]'}`}
                >
                  <FolderTree className="w-4 h-4" />
                  By company
                </button>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <div className="spinner w-8 h-8" />
              </div>
            ) : viewMode === 'byCompany' && canAccessBackOfficeUsers ? (
              <div className="overflow-x-auto space-y-2">
                {groupUsersByCompany().map((group) => {
                  const isExpanded = expandedCompanies.has(group.merchant_id);
                  return (
                    <div key={group.merchant_id} className="rounded-xl border border-[#DCE4E0] overflow-hidden">
                      <button
                        type="button"
                        onClick={() => toggleCompanyExpanded(group.merchant_id)}
                        className="w-full flex items-center gap-2 px-4 py-3 bg-[#F8FAFC] hover:bg-[#E6F7F3] text-left font-medium text-[#073B2E] transition-colors"
                      >
                        {isExpanded ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
                        <span className="truncate">{group.company_name}</span>
                        <span className="text-slate-500 font-normal text-sm">({group.users.length} user{group.users.length !== 1 ? 's' : ''})</span>
                      </button>
                      {isExpanded && (
                        <div className="border-t border-[#DCE4E0]">
                          <table className="w-full" data-testid="users-table-by-company">
                            <thead>
                              <tr>
                                <th className="table-header">User</th>
                                <th className="table-header">Role</th>
                                <th className="table-header">Status</th>
                                <th className="table-header">2FA</th>
                                <th className="table-header">Joined</th>
                                <th className="table-header">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {group.users.map((user) => (
                                <tr key={user.id} className="table-row">
                                  <td className="table-cell cursor-pointer hover:bg-slate-50/50" onClick={() => openUserDetail(user.id)} title="View details">
                                    <div className="flex items-center gap-3">
                                      <div className="w-10 h-10 bg-[#E6F7F3] rounded-full flex items-center justify-center">
                                        <span className="font-semibold text-[#1C472B]">{user.name?.charAt(0).toUpperCase()}</span>
                                      </div>
                                      <div>
                                        <p className="font-medium text-[#0F172A]">{user.name}</p>
                                        <p className="text-sm text-slate-500">{user.email}</p>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="table-cell" onClick={(e) => e.stopPropagation()}>
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <button className={`px-3 py-1 rounded-full text-xs font-semibold capitalize cursor-pointer hover:opacity-80 ${getRoleBadgeClass(user.role)}`} disabled={!canChangeRole(user)}>
                                          {getRoleLabel(user.role)}
                                        </button>
                                      </DropdownMenuTrigger>
                                      {canChangeRole(user) && (
                                        <DropdownMenuContent>
                                          {getRoleOptionsForUser(user).map((r) => (
                                            <DropdownMenuItem key={r} onClick={() => handleUpdateRole(user.id, r)}>
                                              {r === 'admin' && <Shield className="w-4 h-4 mr-2" />}
                                              {r === 'compliance_officer' && <FileCheck className="w-4 h-4 mr-2" />}
                                              {r === 'accounts' && <DollarSign className="w-4 h-4 mr-2" />}
                                              {r === 'operations' && <Settings className="w-4 h-4 mr-2" />}
                                              {r === 'merchant_admin' && <Store className="w-4 h-4 mr-2" />}
                                              {r === 'manager' && <Users className="w-4 h-4 mr-2" />}
                                              {r === 'viewer' && <UserCheck className="w-4 h-4 mr-2" />}
                                              {getRoleLabel(r)}
                                            </DropdownMenuItem>
                                          ))}
                                        </DropdownMenuContent>
                                      )}
                                    </DropdownMenu>
                                  </td>
                                  <td className="table-cell">
                                    <span className={user.is_verified ? 'badge-success' : 'badge-pending'}>{user.is_verified ? 'Verified' : 'Pending'}</span>
                                  </td>
                                  <td className="table-cell" onClick={(e) => e.stopPropagation()}>
                                    {currentUser?.role === 'admin' && user.is_2fa_enabled ? (
                                      <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                          <button className="text-[#1C472B] flex items-center gap-1 px-2 py-1 rounded-md hover:bg-[#E6F7F3] cursor-pointer text-left">
                                            <Shield className="w-4 h-4" />Enabled
                                          </button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent>
                                          <DropdownMenuItem onClick={() => handleAdminDisable2FA(user.id)}>
                                            Disable 2FA (user can log in & re-setup)
                                          </DropdownMenuItem>
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                    ) : user.is_2fa_enabled ? (
                                      <span className="text-[#1C472B] flex items-center gap-1"><Shield className="w-4 h-4" />Enabled</span>
                                    ) : (
                                      <span className="text-slate-400">Disabled</span>
                                    )}
                                  </td>
                                  <td className="table-cell text-slate-500">{formatDate(user.created_at)}</td>
                                  <td className="table-cell" onClick={(e) => e.stopPropagation()}>
                                    {user.id !== currentUser?.id && (
                                      <AlertDialog>
                                        <AlertDialogTrigger asChild>
                                          <button className="p-2 hover:bg-red-50 rounded-lg transition-colors"><Trash2 className="w-4 h-4 text-[#F08080]" /></button>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent>
                                          <AlertDialogHeader>
                                            <AlertDialogTitle>Delete User?</AlertDialogTitle>
                                            <AlertDialogDescription>This will permanently delete {user.name}'s account. This action cannot be undone.</AlertDialogDescription>
                                          </AlertDialogHeader>
                                          <AlertDialogFooter>
                                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                                            <AlertDialogAction onClick={() => handleDeleteUser(user.id)} className="btn-destructive">Delete</AlertDialogAction>
                                          </AlertDialogFooter>
                                        </AlertDialogContent>
                                      </AlertDialog>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="users-table">
                  <thead>
                    <tr>
                      <th className="table-header">User</th>
                      {currentUser?.role === 'admin' && <th className="table-header">Company</th>}
                      <th className="table-header">Role</th>
                      <th className="table-header">Status</th>
                      <th className="table-header">2FA</th>
                      <th className="table-header">Joined</th>
                      <th className="table-header">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id} className="table-row">
                        <td
                          className="table-cell cursor-pointer hover:bg-slate-50/50"
                          onClick={() => openUserDetail(user.id)}
                          title="View details / temp password"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-[#E6F7F3] rounded-full flex items-center justify-center">
                              <span className="font-semibold text-[#1C472B]">
                                {user.name?.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-[#0F172A]">{user.name}</p>
                              <p className="text-sm text-slate-500">{user.email}</p>
                            </div>
                          </div>
                        </td>
                        {canAccessBackOfficeUsers && (
                          <td className="table-cell text-slate-600" title={user.company_name}>
                            {user.company_name || '—'}
                          </td>
                        )}
                        <td className="table-cell" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                className={`px-3 py-1 rounded-full text-xs font-semibold capitalize cursor-pointer hover:opacity-80 ${getRoleBadgeClass(
                                  user.role
                                )}`}
                                disabled={!canChangeRole(user)}
                              >
                                {getRoleLabel(user.role)}
                              </button>
                            </DropdownMenuTrigger>
                            {canChangeRole(user) && (
                              <DropdownMenuContent>
                                {getRoleOptionsForUser(user).map((r) => (
                                  <DropdownMenuItem key={r} onClick={() => handleUpdateRole(user.id, r)}>
                                    {r === 'admin' && <Shield className="w-4 h-4 mr-2" />}
                                    {r === 'compliance_officer' && <FileCheck className="w-4 h-4 mr-2" />}
                                    {r === 'accounts' && <DollarSign className="w-4 h-4 mr-2" />}
                                    {r === 'operations' && <Settings className="w-4 h-4 mr-2" />}
                                    {r === 'merchant_admin' && <Store className="w-4 h-4 mr-2" />}
                                    {r === 'manager' && <Users className="w-4 h-4 mr-2" />}
                                    {r === 'viewer' && <UserCheck className="w-4 h-4 mr-2" />}
                                    {getRoleLabel(r)}
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuContent>
                            )}
                          </DropdownMenu>
                        </td>
                        <td className="table-cell">
                          <span
                            className={
                              user.is_verified ? 'badge-success' : 'badge-pending'
                            }
                          >
                            {user.is_verified ? 'Verified' : 'Pending'}
                          </span>
                        </td>
                        <td className="table-cell" onClick={(e) => e.stopPropagation()}>
                          {currentUser?.role === 'admin' && user.is_2fa_enabled ? (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button className="text-[#1C472B] flex items-center gap-1 px-2 py-1 rounded-md hover:bg-[#E6F7F3] cursor-pointer text-left">
                                  <Shield className="w-4 h-4" />Enabled
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent>
                                <DropdownMenuItem onClick={() => handleAdminDisable2FA(user.id)}>
                                  Disable 2FA (user can log in & re-setup)
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          ) : user.is_2fa_enabled ? (
                            <span className="text-[#1C472B] flex items-center gap-1">
                              <Shield className="w-4 h-4" />
                              Enabled
                            </span>
                          ) : (
                            <span className="text-slate-400">Disabled</span>
                          )}
                        </td>
                        <td className="table-cell text-slate-500">
                          {formatDate(user.created_at)}
                        </td>
                        <td className="table-cell" onClick={(e) => e.stopPropagation()}>
                          {user.id !== currentUser?.id && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <button className="p-2 hover:bg-red-50 rounded-lg transition-colors">
                                  <Trash2 className="w-4 h-4 text-[#F08080]" />
                                </button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete User?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will permanently delete {user.name}'s account. This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDeleteUser(user.id)}
                                    className="btn-destructive"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </LayoutComponent>
  );
};

export default UserManagement;
